import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchRecentEventPhotos,
  fetchHomeSnapshot,
  readCachedHomeEvents,
  writeCachedHomeEvents,
  buildThumbUrl,
  buildOriginalUrl,
} from "../../../api/publicPhotos";

// Homepage "Recent Event Photos" section (public, no login). Renders
// BELOW the hero — the JOIN button paints immediately and this section
// fills in whenever the (cached) public API responds. Renders nothing
// at all until there's data, so a slow or empty response never shifts
// the page around.

const sectionStyle = {
  background: "#fff",
  padding: "40px 16px 48px",
};

const cardStyle = {
  border: "1px solid #e6e9f0",
  borderRadius: 12,
  overflow: "hidden",
  background: "#fff",
  cursor: "pointer",
  textAlign: "left",
  padding: 0,
  width: "100%",
  boxShadow: "0 1px 4px rgba(20,20,40,0.06)",
};

const fmtDate = (raw, socialEventId) => {
  const src =
    raw ||
    (String(socialEventId || "").match(/^(\d{4}-\d{2}-\d{2})-/) || [])[1];
  if (!src) return "";
  const d = new Date(src);
  if (Number.isNaN(d.getTime())) return String(src);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const titleFromId = (socialEventId) =>
  String(socialEventId || "")
    .replace(/^\d{4}-\d{2}-\d{2}-/, "")
    .replace(/-/g, " ");

// Progressive photo: paint the small _thumb.jpg immediately (something
// beats a blank square while the big file downloads), then swap in the
// full-quality original once it has fully loaded. The original's
// download only starts after the thumb renders, so lazy-loading still
// keeps offscreen cards from fetching anything. If a legacy photo has
// no thumb, the error handler jumps straight to the original.
const ProgressivePhoto = ({ s3Key, alt }) => {
  const thumb = buildThumbUrl(s3Key);
  const original = buildOriginalUrl(s3Key);
  const [src, setSrc] = useState(thumb);

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      style={{
        width: "100%",
        aspectRatio: "1 / 1",
        objectFit: "cover",
        display: "block",
      }}
      onLoad={() => {
        if (src !== thumb) return; // already showing the original
        const full = new Image();
        full.onload = () => setSrc(original);
        full.src = original;
      }}
      onError={() => {
        if (src !== original) setSrc(original);
      }}
    />
  );
};

// Shared event-card grid — the homepage section and the public "all
// event photos" page render the same cards from the same API shape.
export const EventPhotoCardGrid = ({ events }) => {
  const navigate = useNavigate();
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
        gap: 16,
      }}
    >
      {events.map((ev) => (
        <button
          key={ev.socialEventId}
          type="button"
          style={cardStyle}
          onClick={() => navigate(`/photos/${ev.socialEventId}`)}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                ev.photos.length > 1 ? "1fr 1fr" : "1fr",
              gap: 2,
            }}
          >
            {ev.photos.slice(0, 4).map((p) => (
              <ProgressivePhoto
                key={p.s3Key}
                s3Key={p.s3Key}
                alt={p.displayName || "Event photo"}
              />
            ))}
          </div>
          <div style={{ padding: "10px 12px" }}>
            <div
              style={{
                fontWeight: 700,
                color: "#222",
                fontSize: 15,
                textTransform: "capitalize",
              }}
            >
              {ev.title || titleFromId(ev.socialEventId)}
            </div>
            <div style={{ color: "#6a7385", fontSize: 13 }}>
              {fmtDate(ev.date, ev.socialEventId)} · {ev.photoCount}{" "}
              photo{ev.photoCount === 1 ? "" : "s"} · see all →
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

const RecentEventPhotos = () => {
  const navigate = useNavigate();
  // Three-stage paint, each stage only ever getting fresher:
  //   1. localStorage (0ms) — returning visitors see titles + thumbs
  //      instantly, before any network.
  //   2. Static S3 snapshot (~100-300ms) — no API Gateway, no Lambda
  //      cold start; rewritten by the backend on every photo change.
  //   3. The live API — source of truth; wins whenever it responds and
  //      refreshes the localStorage copy for next time.
  const [events, setEvents] = useState(() => readCachedHomeEvents() || []);

  useEffect(() => {
    let alive = true;
    let apiLanded = false;

    fetchHomeSnapshot()
      .then((data) => {
        // Never let the snapshot overwrite a fresher API response.
        if (alive && !apiLanded && Array.isArray(data.events)) {
          setEvents(data.events);
        }
      })
      .catch(() => {
        // Snapshot missing (first deploy) or unreadable — API covers us.
      });

    fetchRecentEventPhotos()
      .then((data) => {
        apiLanded = true;
        if (alive && Array.isArray(data.events)) {
          setEvents(data.events);
          writeCachedHomeEvents(data.events);
        }
      })
      .catch(() => {
        // Public homepage — fail silently; whatever stage already
        // painted (cache/snapshot) stays up.
      });

    return () => {
      alive = false;
    };
  }, []);

  if (!events.length) return null;

  return (
    <div style={sectionStyle}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <h2
          style={{
            textAlign: "center",
            fontFamily: "var(--ab-font-display)",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            fontWeight: 400,
            fontSize: 36,
            color: "var(--ab-ink)",
            marginBottom: 6,
          }}
        >
          📷 Recent Event Photos
        </h2>
        <p
          style={{
            textAlign: "center",
            color: "#6a7385",
            marginBottom: 22,
            fontSize: 15,
          }}
        >
          Tap an event to see (and download) all its photos
        </p>
        <EventPhotoCardGrid events={events} />
        <div style={{ textAlign: "center", marginTop: 22 }}>
          <button
            type="button"
            onClick={() => navigate("/photos/events")}
            style={{
              background: "none",
              border: "2px solid var(--ab-ocean)",
              color: "var(--ab-ocean)",
              borderRadius: 999,
              padding: "10px 26px",
              fontWeight: 700,
              fontSize: 15,
              cursor: "pointer",
            }}
          >
            View all event photos →
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecentEventPhotos;
